// Workflows (spec §9, §47; ADR 0020): reusable multi-step processes built
// from pieces Hub already has. A template is versioned: a published version
// is frozen, and every run keeps the version it started with. Each step
// either does something through the module that owns it (stamp a checklist,
// ask for an approval, open a task, change a status through that module's
// own function, notify) or waits for something to be true (a checklist
// complete, an approval decided, a task done, a form or document received,
// a date). A run never does more than the person who started it may do: a
// step that needs a capability they lack pauses and says which.
//
// Out of scope by the owner's decision: purchasing. Official purchase
// orders live in QuickBooks, so there is no purchase order step or record.

import { z } from 'zod'

const ID = z.string().min(1).max(100)
const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')

export const WORKFLOW_SUBJECT_TYPES = ['person', 'organization', 'vendor', 'event', 'facility', 'grant', 'form_submission'] as const
export type WorkflowSubjectType = (typeof WORKFLOW_SUBJECT_TYPES)[number]
export const WORKFLOW_SUBJECT_LABELS: Record<WorkflowSubjectType, string> = {
  person: 'Person',
  organization: 'Organization',
  vendor: 'Vendor',
  event: 'Event',
  facility: 'Facility',
  grant: 'Grant',
  form_submission: 'Form submission',
}
// Where a subject's page is. A vendor's id is its organization's.
export const WORKFLOW_SUBJECT_PATHS: Record<WorkflowSubjectType, string> = {
  person: 'people',
  organization: 'organizations',
  vendor: 'vendors',
  event: 'events',
  facility: 'facilities',
  grant: 'grants',
  form_submission: 'forms/submissions',
}

export const WORKFLOW_STEP_TYPES = ['checklist', 'approval', 'task', 'form', 'document', 'wait', 'status_change', 'notify'] as const
export type WorkflowStepType = (typeof WORKFLOW_STEP_TYPES)[number]
export const WORKFLOW_STEP_LABELS: Record<WorkflowStepType, string> = {
  checklist: 'Checklist',
  approval: 'Approval',
  task: 'Task',
  form: 'Form received',
  document: 'Document required',
  wait: 'Wait',
  status_change: 'Change status',
  notify: 'Notify',
}

// Which step types make sense on which subject. A checklist and a document
// need a subject that holds requirements and documents; a status change
// needs a subject with a status that its own module changes.
export const STEP_TYPES_FOR: Record<WorkflowSubjectType, readonly WorkflowStepType[]> = {
  person: ['checklist', 'approval', 'task', 'form', 'document', 'wait', 'status_change', 'notify'],
  organization: ['checklist', 'approval', 'task', 'form', 'document', 'wait', 'notify'],
  vendor: ['checklist', 'approval', 'task', 'form', 'document', 'wait', 'status_change', 'notify'],
  event: ['checklist', 'approval', 'task', 'document', 'wait', 'notify'],
  facility: ['checklist', 'approval', 'task', 'document', 'wait', 'notify'],
  grant: ['checklist', 'approval', 'task', 'document', 'wait', 'status_change', 'notify'],
  form_submission: ['approval', 'task', 'form', 'wait', 'notify'],
}

// The statuses a status step may set, per subject, and whose function
// sets them: a vendor's (vendors.manage), a person's staff or volunteer
// record (workforce.manage), a grant's (grants.manage).
export const STATUS_CHANGE_TARGETS = {
  vendor: ['prospective', 'active', 'on_hold', 'inactive'],
  person: ['applicant', 'active', 'inactive', 'alumni'],
  grant: ['prospect', 'researching', 'letter_of_inquiry', 'invited', 'drafting', 'submitted', 'awarded', 'declined', 'withdrawn', 'closed_out'],
} as const satisfies Partial<Record<WorkflowSubjectType, readonly string[]>>

// Who a task is for or a notice goes to. "Run owner" is whoever started
// the run; "subject owner" is the grant's owner or a form submission's
// assignee, and falls back to the run owner when there is none.
export const WORKFLOW_ASSIGNEES = ['run_owner', 'subject_owner', 'person'] as const
export type WorkflowAssignee = (typeof WORKFLOW_ASSIGNEES)[number]

const text = (max: number) => z.string().trim().max(max)

// Step configuration. A draft may be incomplete (empty ids, no approvers);
// publishing checks every step in full.
export const WorkflowStepConfig = z.discriminatedUnion('type', [
  z.object({ type: z.literal('checklist'), templateId: z.string().max(100).default('') }),
  z.object({
    type: z.literal('approval'),
    title: text(300).default(''),
    approverPersonIds: z.array(ID).max(12).default([]),
    rule: z.enum(['all', 'any']).default('all'),
    note: text(2000).nullable().optional(),
  }),
  z.object({
    type: z.literal('task'),
    title: text(300).default(''),
    description: text(2000).nullable().optional(),
    assignee: z.enum(WORKFLOW_ASSIGNEES).default('run_owner'),
    personId: ID.nullable().optional(),
    dueInDays: z.number().int().min(0).max(365).nullable().optional(),
  }),
  z.object({ type: z.literal('form'), formId: z.string().max(100).default(''), instructions: text(1000).nullable().optional() }),
  z.object({
    type: z.literal('document'),
    vendorDocumentKind: z.enum(['w9', 'coi', 'agreement']).nullable().optional(),
    classification: z.enum(['public', 'internal', 'confidential']).nullable().optional(),
    titleContains: text(100).nullable().optional(),
  }),
  z.object({ type: z.literal('wait'), days: z.number().int().min(0).max(365).nullable().optional(), untilDate: DAY.nullable().optional() }),
  z.object({ type: z.literal('status_change'), to: z.string().max(40).default('') }),
  z.object({
    type: z.literal('notify'),
    recipients: z.enum(WORKFLOW_ASSIGNEES).default('run_owner'),
    personIds: z.array(ID).max(10).default([]),
    message: text(500).default(''),
  }),
])
export type WorkflowStepConfig = z.infer<typeof WorkflowStepConfig>

export const WorkflowStep = z.object({ key: z.string().min(1).max(40), label: text(200).min(1), config: WorkflowStepConfig })
export type WorkflowStep = z.infer<typeof WorkflowStep>

export const WorkflowTemplateBody = z.object({ name: text(200).min(1), appliesTo: z.enum(WORKFLOW_SUBJECT_TYPES), description: text(2000).nullable().optional() })
export const WorkflowDraftBody = z.object({
  name: text(200).min(1),
  appliesTo: z.enum(WORKFLOW_SUBJECT_TYPES).optional(),
  description: text(2000).nullable().optional(),
  steps: z.array(WorkflowStep).max(30),
  expectedDraftVersion: z.number().int().min(1),
})
export const WorkflowPublishBody = z.object({ expectedDraftVersion: z.number().int().min(1) })
export const WorkflowRunStartBody = z.object({ templateId: ID, subjectType: z.enum(WORKFLOW_SUBJECT_TYPES), subjectId: ID })
export const WorkflowReasonBody = z.object({ reason: text(1000).min(3) })
export const WorkflowReceivedBody = z.object({ note: text(1000).nullable().optional() })

export const WORKFLOWS_SYSTEM_SUB = 'system:workflows'
export type WorkflowsRunResult = { runsChecked: number; stepsAdvanced: number; errors: string[] }

// ------------------------------------------------------------------ shapes

export const WORKFLOW_RUN_STATUSES = ['running', 'paused', 'completed', 'cancelled'] as const
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number]
export const WORKFLOW_RUN_STATUS_LABELS: Record<WorkflowRunStatus, string> = { running: 'Running', paused: 'Paused', completed: 'Completed', cancelled: 'Cancelled' }

export const WORKFLOW_STEP_STATUSES = ['pending', 'waiting', 'blocked', 'done', 'skipped', 'cancelled'] as const
export type WorkflowStepStatus = (typeof WORKFLOW_STEP_STATUSES)[number]
export const WORKFLOW_STEP_STATUS_LABELS: Record<WorkflowStepStatus, string> = {
  pending: 'Not started',
  waiting: 'Waiting',
  blocked: 'Needs attention',
  done: 'Done',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
}

export type WorkflowTemplateSummary = {
  id: string
  name: string
  appliesTo: WorkflowSubjectType
  description: string | null
  publishedVersion: number | null
  draftChanged: boolean
  stepCount: number
  activeRuns: number
  retiredAt: string | null
  updatedAt: string
}

export type WorkflowTemplateVersion = { version: number; name: string; appliesTo: WorkflowSubjectType; description: string | null; steps: WorkflowStep[]; publishedByName: string | null; publishedAt: string }

export type WorkflowTemplate = WorkflowTemplateSummary & {
  draft: { name: string; description: string | null; steps: WorkflowStep[]; version: number; updatedAt: string }
  versions: WorkflowTemplateVersion[]
  // Problems that would stop the draft from publishing.
  problems: string[]
  can: { manage: boolean; run: boolean }
}

export type WorkflowStepRun = {
  id: string
  position: number
  key: string
  type: WorkflowStepType
  label: string
  status: WorkflowStepStatus
  activatedAt: string | null
  completedAt: string | null
  waitUntil: string | null
  // What the step made or waits on, when the viewer may know: a task, an
  // approval, a checklist stamp, a form.
  target: { type: string; id: string; path: string | null } | null
  blockedReason: string | null
  needsCapability: string | null
  skipReason: string | null
  skippedByName: string | null
  actedByName: string | null
  manualReceipt: boolean
}

export type WorkflowRunSummary = {
  id: string
  templateId: string
  templateName: string
  templateVersion: number
  subjectType: WorkflowSubjectType
  subjectId: string
  subjectLabel: string | null
  status: WorkflowRunStatus
  startedByName: string | null
  startedVia: 'manual' | 'form_routing'
  currentStep: { position: number; label: string; status: WorkflowStepStatus } | null
  stepCount: number
  doneCount: number
  createdAt: string
  updatedAt: string
}

export type WorkflowRun = WorkflowRunSummary & {
  steps: WorkflowStepRun[]
  cancelReason: string | null
  sourceSubmissionId: string | null
  can: { act: boolean }
}
